import { useCallback } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import { useGlobalState } from '@pepper/ui'
import { useIsMutating } from '@tanstack/react-query'
import { Icon } from '@iconify/react'

import DeleteItemModal from '../DeleteModal/DeleteItemModal'

import { useGetWorkflowOnOffState } from 'utils/hooks/workflows/useGetWorkflowToggledState'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from 'components/workflows/StatusDropdown'
import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'

type Props = {
  items: Item[]
  selectedItems: Item[]
  selectedItemsStage: AssignmentStage | null | ''
  companyId: string
  setSelectedItems: (items: Item[]) => void
  onDownload: (items: Item[]) => void
  refetchRecords: () => void
  assigneeList?: TeamMembers[]
  workflowStatusList?: WorkflowStatus[]
  handleDocsDownload?: (items: Item[]) => void
}

const ItemsBottomPopup = ({
  items,
  selectedItems,
  selectedItemsStage,
  companyId,
  setSelectedItems,
  onDownload,
  refetchRecords,
  handleDocsDownload,
  assigneeList,
  workflowStatusList,
}: Props) => {
  const { bulkDownloadJob } = useGlobalState()
  const isMutatingBulkDownloads = useIsMutating({
    mutationKey: ['bulk-export'],
  })
  const { show: showDeleteModal } = useModal(DeleteItemModal)
  const { data: toggleState } = useGetWorkflowOnOffState({ companyId })

  const deletable = selectedItems.every(
    item => item.item_type === 'private_doc'
  )

  const selectAllItemsText = () => {
    if (selectedItemsStage === '') {
      return 'Select all (Docs)'
    } else {
      return 'Select all'
    }
  }

  const handleClear = () => {
    setSelectedItems([])
  }

  const handleDelete = () => {
    showDeleteModal({
      items: selectedItems,
      onSuccess: refetchRecords,
      onClose: handleClear,
    })
  }

  const handleDownloadItems = () => {
    onDownload(selectedItems)
  }

  const handleSelectAllItems = useCallback(() => {
    if (selectedItemsStage) {
      const _items = items.filter(item => item.stage === selectedItemsStage)
      setSelectedItems(_items)
    } else if (selectedItemsStage === '') {
      const _items = items.filter(item => item.stage === '')
      setSelectedItems(_items)
    } else {
      setSelectedItems(items)
    }
  }, [items, selectedItemsStage])

  return (
    <BottomPopup selectedCount={selectedItems.length} onClose={handleClear}>
      <BottomPopupBtn
        onClick={handleSelectAllItems}
        data-id="business-assignments-select-all-button"
      >
        {selectAllItemsText()}
      </BottomPopupBtn>

      {toggleState?.data.workflow_enabled === 1 && (
        <>
          <AssigneeDropdown
            companyId={companyId}
            assigneeList={assigneeList}
            bulkItems={selectedItems}
            isPrivateDoc={deletable}
            showTooltip={true}
            showSelected={false}
          >
            <BottomPopupBtn
              iconPlacement="right"
              icon={<Icon icon="lucide:chevron-down" />}
            >
              Select Assignee
            </BottomPopupBtn>
          </AssigneeDropdown>

          <StatusDropdown
            companyId={companyId}
            statusList={workflowStatusList}
            bulkItems={selectedItems}
            isPrivateDoc={deletable}
          >
            <BottomPopupBtn
              iconPlacement="right"
              icon={<Icon icon="lucide:chevron-down" />}
            >
              Select Status
            </BottomPopupBtn>
          </StatusDropdown>
        </>
      )}

      <BottomPopupBtn
        onClick={handleDownloadItems}
        data-id="business-assignments-download-button"
        icon={<Icon icon="fe:download" width={14} />}
      >
        Export Docs Links
      </BottomPopupBtn>

      {handleDocsDownload && selectedItems.length > 0 && (
        <BottomPopupBtn
          onClick={() => handleDocsDownload(selectedItems)}
          disabled={
            bulkDownloadJob?.status === 'pending' || isMutatingBulkDownloads > 0
          }
          icon={<Icon icon="uil:file-download" width="14" />}
        >
          Download Docs
        </BottomPopupBtn>
      )}
      {deletable && <BottomPopupBtn type="delete" onClick={handleDelete} />}
    </BottomPopup>
  )
}

export default ItemsBottomPopup
